import React, {FC, useEffect, useState} from 'react';
import {useAppSelector} from 'redux/hooks';

import {t} from 'i18next';
import {Button, Input} from "../../commons";
import {Field, withFormik} from "formik";
import Validations from "./Validations";
import store, {RootState} from "../../../redux/store";
import ToggleButton from "../../Toggle/Toggle";
import {IStocksExportRuleForWarehouse} from "../../../constants/StocksExport/IStocksExportRuleForWarehouse";
import Rows from "./Rows/Rows";
import {saveStocksExport} from "../../../redux/stocksExport/reducer";
import stocksExportService from "../../../api/services/stocksExportService";
import {IStocksExportSettings} from "../../../constants/StocksExport/IStocksExportSettings";
import warehousesService from "../../../api/services/warehousesService";
import {IWarehouse} from "../../../constants/Warehouses/IWarehouse";
import Error from "./Error";
import {IStocksReserveSettings} from "../../../constants/StocksReserve/IStocksReserveSettings";
import {IStocksReserveRuleForWarehouse} from "../../../constants/StocksReserve/IStocksReserveRuleForWarehouse";
import stocksReserveService from "../../../api/services/stocksReserveService";
import ExportStocksToWarehouses from "../../Tables/StocksExportTable/Actions/ExportStocksToWarehouses";
import {IStocksWriteOffWarehouse} from "../../../constants/StocksReserve/IStocksWriteOffWarehouse";
import {saveStocksReserveSettings} from "../../../redux/stocksReserve/reducer";
import {createSelector} from "reselect";
import ReservesOnlyForHighSla from "./ReservesOnlyForHighSla";
import sellerProductsService, {IProductsFilter} from "../../../api/services/sellerProductsService";

interface StocksExportFormFields {
  isFullExport: boolean,
  export: IStocksExportSettings,
  reserve: IStocksReserveSettings
}

interface StocksExportFormProps {
  values: StocksExportFormFields,
  touched: any,
  errors: any,
  isSubmitting: any,
  handleChange: any,
  handleBlur: any,
  handleSubmit: any,
  resetForm: any,
  setValues: any,
  setModalOpen: (param: boolean) => void,
  setFieldValue: any,
  isNeedClearWarehouseForm: boolean,
  setIsNeedClearWarehouseForm: (param: boolean) => void,
}

const initialFields: StocksExportFormFields = {
  isFullExport: false,
  export: {
    uuid: null,
    sellerID: null,
    rulesForWarehouses: [],
    isEnabledAutoExport: true,
  },
  reserve: {
    uuid: null,
    sellerID: null,
    rulesForWarehouses: [],
    isEnabledAutoReserving: false
  },

}

const memoizedSelector = createSelector(
    (state: RootState) => state.sellers.currentSeller,
    (currentSeller) => ({ currentSeller })
);

const InnerForm: FC<StocksExportFormProps> = ({
  values,
  touched,
  errors,
  isSubmitting,
  handleChange,
  handleBlur,
  handleSubmit,
  resetForm,
  setValues,
  setModalOpen,
  setFieldValue,
}) => {

  const { currentSeller } = useAppSelector(memoizedSelector)

  const [warehouses, setWarehouses] = React.useState<IWarehouse[]>([]);
  const [setting, setSetting] = React.useState<IStocksExportSettings|null>();
  const [reserveSetting, setReserveSetting] = React.useState<IStocksReserveSettings|null>();
  const [dataLoadingError, setDataLoadingError] = React.useState<string>();
  const [isDataLoading, setIsDataLoading] = React.useState<boolean>(true);
  const [isEnabledAutoExport, setIsEnabledAutoExport] = useState<boolean>(false)
  const [isEnabledAutoReserving, setIsEnabledAutoReserving] = useState<boolean>(false)
  const [isDistributedAllForAll, setIsDistributedAllForAll] = useState<boolean>(false)
  const [rulesInitialShares, setRulesInitialShares] = useState<{[key: string]: number}>({})
  const [rulesModel, setRulesModel] = useState<IStocksExportRuleForWarehouse[]>([])
  const [reserveRulesModel, setReserveRulesModel] = useState<IStocksReserveRuleForWarehouse[]>([])
  const [freeShare, setFreeShare] = useState<number>(100)
  const [isFilteringAvailableForSLA, setIsFilteringAvailableForSLA] = useState<boolean>(false)
  const [isReservingNotAvailableForSLA, setIsReservingNotAvailableForSLA] = useState<boolean>(false)
  const [sellerID, setSellerID] = useState<number>(0)
  const [productFilters, setProductFilters] = React.useState<IProductsFilter[]>([]);

  useEffect(() => {
    if (!currentSeller.info.data?.id) {
      return
    }

    //
    setSellerID(currentSeller.info.data?.id)

    //
    setIsReservingNotAvailableForSLA(!['high', 'increased'].includes(currentSeller.info.data?.sla || ''))
    setIsFilteringAvailableForSLA(['high', 'increased'].includes(currentSeller.info.data?.sla || ''))
  }, [currentSeller])

  useEffect(() => {

    if (!sellerID || (reserveSetting?.uuid && reserveSetting.uuid.length > 0)) {
      return
    }

    // Prepare stocks export setting
    stocksExportService.getSettingsForSeller(sellerID)
        .then(
            async (res: { data: { success: boolean, data: IStocksExportSettings } }) => {
              if (res.data.success) {
                setSetting(res.data.data)

                setIsDataLoading(false)
              } else {
                setDataLoadingError('Не удалось загрузить настройки');
                setIsDataLoading(false)
              }
            },
            (err) => {
              setDataLoadingError(err.response.data.error);
              setIsDataLoading(false)
            }
        )
        .catch((err) => {
          setDataLoadingError(err.message);
          setIsDataLoading(false);
        })
        .finally(() => {
          // Prepare stocks reserve setting
          stocksReserveService.getSettingsForSeller(sellerID)
              .then(
                  async (res: { data: { success: boolean, data: IStocksReserveSettings } }) => {
                    if (res.data.success) {
                      setReserveSetting(res.data.data)

                      // Prepare warehouses export rules
                      setWarehouses(((await warehousesService.getWarehousesForSeller(sellerID))?.data?.data) || []);

                      setIsDataLoading(false)
                    } else {
                      setDataLoadingError('Не удалось загрузить настройки');
                      setIsDataLoading(false)
                    }
                  },
                  (err) => {
                    setDataLoadingError(err.response.data.error);
                    setIsDataLoading(false)
                  }
              )
              .catch((err) => {
                setDataLoadingError(err.message);
                setIsDataLoading(false);
              })
              .finally(() => setIsDataLoading(false))
        })

        // Prepare product filters
        sellerProductsService.getProductFilters(sellerID)
        .then(
            async (res: { data: { success: boolean, data: IProductsFilter[] } }) => {
              if (res.data.success) {
                setProductFilters(res.data.data.filter(f => !f.isDeleted))
              } else {
                setDataLoadingError('Не удалось загрузить список фильтров продуктов');
              }
            },
            (err) => {
              setDataLoadingError(err.response.data.error);
            }
        )
        .catch((err) => {
          setDataLoadingError(err.message);
        })

  }, [sellerID])

  useEffect(() => {
    if (setting) {
      setFieldValue('export.uuid', setting.uuid)

      setIsEnabledAutoExport(setting.isEnabledAutoExport)

      const rules = setting.rulesForWarehouses
          .map(r => {
              const wh = warehouses.filter(w => w.uuid === r.warehouseID)[0] || {}
              r.warehouse = wh
              return r
          })

          .filter(r => r.warehouse?.uuid != undefined)

      setRulesModel(rules)

      rules.forEach(r => {
        setRulesInitialShares(rulesInitialShares => ({
          ...rulesInitialShares,
          [r.warehouseID]: r.percentageOfTotal
        }))
      })
    }

    if (reserveSetting) {
      setIsEnabledAutoReserving(reserveSetting.isEnabledAutoReserving)

      setFieldValue('reserve.uuid', reserveSetting.uuid)

      // Обогащаем/синхронизируем настройки для складов из настроек "Отправки"
      if (setting) {
        setting.rulesForWarehouses.forEach(exportWhRule => {
          const reserveRulesForWh = reserveSetting.rulesForWarehouses.filter(reserveWhRule => reserveWhRule.warehouseID === exportWhRule.warehouseID)
          if (reserveRulesForWh.length === 0) {
            reserveSetting.rulesForWarehouses.push({
              isReservingByDeltaAndOrder: false,
              isReservingByOrder: false,
              isWriteOffAllWhs: false,
              reservingFromDate: "",
              stocksWriteOffWarehouses: [],
              warehouse: exportWhRule.warehouse,
              warehouseID: exportWhRule.warehouseID
            })
          }
        })
      }

      //
      const reserveRules = reserveSetting.rulesForWarehouses
          .map(r => {
            // Обогощение объктом склада, для дальнейшего удобства при отрисовке интерфейса
            r.warehouse = warehouses.filter(w => w.uuid === r.warehouseID)[0] || {} as IWarehouse

            (r.stocksWriteOffWarehouses || ([] as IStocksWriteOffWarehouse[])).map(sww => {
              sww.warehouse = warehouses.filter(w => w.uuid === sww.warehouseID)[0] || {}
              return sww
            })

            return r
          })

          .filter(r => r.warehouse?.uuid != undefined)

      setReserveRulesModel(reserveRules)
    }

  }, [warehouses])

  useEffect(() => {
    setFieldValue('export.sellerID', sellerID)
    setFieldValue('reserve.sellerID', sellerID)
  }, [sellerID])

  useEffect(() => {
    setFieldValue('export.isEnabledAutoExport', isEnabledAutoExport)
  }, [isEnabledAutoExport])

  useEffect(() => {
    setFieldValue('reserve.isEnabledAutoReserving', isEnabledAutoReserving)
  }, [isEnabledAutoReserving])

  useEffect(() => {
    console.log('Set rules for wh', rulesModel)
    // setFreeShare(100 - rulesModel.reduce(
    //     (accumulator: number, v: IStocksExportRuleForWarehouse) =>  accumulator + v.percentageOfTotal, 0
    // ))

    // setIsDistributedAllForAll(rulesModel.reduce(
    //     (accumulator: number, v: IStocksExportRuleForWarehouse) =>  accumulator + (v.percentageOfTotal || 100), 0
    // ) === rulesModel.length * 100)
    setFieldValue('export.rulesForWarehouses', rulesModel)
  }, [rulesModel])

  useEffect(() => {
    console.log('Set reserve rules for wh', reserveRulesModel)
    // setFreeShare(100 - rulesModel.reduce(
    //     (accumulator: number, v: IStocksExportRuleForWarehouse) =>  accumulator + v.percentageOfTotal, 0
    // ))

    // setIsDistributedAllForAll(rulesModel.reduce(
    //     (accumulator: number, v: IStocksExportRuleForWarehouse) =>  accumulator + (v.percentageOfTotal || 100), 0
    // ) === rulesModel.length * 100)
    setFieldValue('reserve.rulesForWarehouses', reserveRulesModel)
  }, [reserveRulesModel])

  function updateSharingValues() {
    if (rulesModel && rulesModel.length > 0) {
      const distributingRules = rulesModel.filter(r => r.isDistribute)
      const rulesCount = distributingRules.length
      const isEnabledUniformDistribution = rulesCount > 0
      // if (isEnabledUniformDistribution) {
        let freeShareForUniformDistribution = 100
        if (freeShareForUniformDistribution < 0) {
          freeShareForUniformDistribution = 0
        }
        const sharePerRule = freeShareForUniformDistribution / rulesCount

        let isFirstDistributingItemUpdated = false
        setRulesModel(rulesModel.map((r, i) => {
          let v = r.percentageOfTotal
          v = v < 0 ? 0 : v > 100 ? 100 : v
          if (r.isDistribute) {
            v = !isFirstDistributingItemUpdated ? Math.ceil(sharePerRule) : Math.floor(sharePerRule)
          }
          isFirstDistributingItemUpdated = true
          return {...r, percentageOfTotal: v}
        }))

      // } else {
      //
      //   setRulesModel(rulesModel.map((r, i) => {
      //     return {...r, percentageOfTotal: rulesInitialShares[r.warehouseID]}
      //   }))
      //
      // }
    }
  }

  function updateRulesInValues(rulesModel: IStocksExportRuleForWarehouse[]): void {
    setRulesModel(rulesModel)
    updateSharingValues()
  }

  function updateReserveRulesInValues(rulesModel: IStocksReserveRuleForWarehouse[]): void {
    setReserveRulesModel(rulesModel)
  }

  function handleSave(type: 'saveAndExport'|'save') {
    setFieldValue('isFullExport', type === 'saveAndExport')
    handleSubmit()
  }

  const customInputClassName = 'border-[#E2E8F0] mb-1 px-3 py-2 rounded-md text-sm shadow-sm placeholder-slate-400  w-full focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'
  return (
      // @ts-ignore
      <form className="w-full" id="stocks-export-form">
        {dataLoadingError &&
            <Error message={'Не удалось загрузить настройки. Попробуйте обновить страницу позже'}></Error>
        }
        {!dataLoadingError &&
            <div>

              <div className="flex mb-4 mt-8">
                <ToggleButton
                    toggleHandler={setIsEnabledAutoExport}
                    isChecked={isEnabledAutoExport}
                />
                <span className={`pl-2 text-sm`}>Разрешить отправку остатков</span>
              </div>

              <div className="flex mb-4">
                <ToggleButton
                    toggleHandler={setIsEnabledAutoReserving}
                    isChecked={isEnabledAutoReserving}
                    disabled={isReservingNotAvailableForSLA}
                />
                <span className={`pl-2 text-sm`}>Включить резервирование и автоматическую синхронизацию между каналами</span>
              </div>

              {isReservingNotAvailableForSLA &&
                  <ReservesOnlyForHighSla/>
              }
              <div className="mb-4">
                  <Rows
                      sellerID={sellerID}
                      isEnabledAutoExport={isEnabledAutoExport}
                      isEnabledAutoReserving={isEnabledAutoReserving}
                      isDisabledAutoReserving={isReservingNotAvailableForSLA}
                      isFilteringAvailableForSLA={isFilteringAvailableForSLA}
                      data={rulesModel}
                      reserveData={reserveRulesModel}
                      updateDataHandler={updateRulesInValues}
                      updateReserveDataHandler={updateReserveRulesInValues}
                      freeShare={freeShare}
                      productFilters={productFilters}
                  ></Rows>

              </div>

              <div className="flex justify-start absolute bottom-0 border-t-[1px] px-12 w-full sm:min-w-[511px] right-0 py-3 bg-white">
                <Button
                    type="button"
                    clickHandler={() => handleSave('saveAndExport')}
                    appearance="primary"
                    customClassName="w-1/2 sm:w-1/2 py-2.5"
                    loading={isSubmitting}
                >
                  {t<string>("Сохранить и отправить остатки", {ns: "messages"})}
                </Button>
                <Button
                    type="button"
                    clickHandler={() => handleSave('save')}
                    customClassName="ml-5 text-sm underline"
                    loading={isSubmitting}
                >
                  {t<string>("Сохранить настройки", {ns: "messages"})}
                </Button>
              </div>
            </div>
        }

      </form>
  );
};

//@ts-ignore
const FormStocksExport = withFormik({
  enableReinitialize: true,

  mapPropsToValues: () => (initialFields),

  validate: (values: StocksExportFormFields, props) => {
    let errors = {};

    console.log('Validation', values)

    const validation = Validations();

    //@ts-ignore
    Object.keys(values).forEach((key) => validation[key] && validation[key](errors, values[key]))

    return errors;
  },

  handleSubmit: (values: StocksExportFormFields, { setSubmitting }) => {
    setSubmitting(true);
    setTimeout(() => {
      console.log('Submission', values);

      //@ts-ignore
      store.dispatch(saveStocksExport({data: values.export, isFullExport: values.isFullExport}))
      //@ts-ignore
      store.dispatch(saveStocksReserveSettings(values.reserve))

      setSubmitting(false);
    }, 500)
  },
  //@ts-ignore
})(InnerForm)

export default FormStocksExport;